import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "../../lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Fixed/Flex
      "fixed inset-0 z-50 " +
      // Data state
      "data-[state=open]:animate-in data-[state=closed]:animate-out " +
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
      // Other
      "bg-black/80",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const dialogWidthVariants = {
  xs: 'sm:max-w-md',
  sm: 'sm:max-w-xl',
  md: 'sm:max-w-4xl',
  lg: 'sm:max-w-6xl',
  none: '',
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    width?: 'xs' | 'sm' | 'md' | 'lg' | 'none'
    noMinHeight?: boolean
    overlayClassName?: string
    showCloseButton?: boolean
    noCloseButton?: boolean
  }
>(({ className, children, 'aria-describedby': ariaDescribedBy, width = 'md', noMinHeight = false, overlayClassName, showCloseButton = true, noCloseButton = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile: Fixed/Flex
        "fixed inset-0 z-50 flex flex-col justify-center sm:justify-normal",
        // Mobile: Width rules
        "w-full",
        // Mobile: Height rules
        "h-full overflow-y-auto custom-scrollbar",
        // Mobile: Data state
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        // Mobile: Other
        "bg-card p-6 shadow-lg duration-200",
        // sm: Fixed/Flex
        "sm:inset-auto sm:left-[50%] sm:top-[50%] sm:right-auto sm:bottom-auto",
        "sm:translate-x-[-50%] sm:translate-y-[-50%]",
        // sm: Width rules
        dialogWidthVariants[width],
        // sm: Height rules
        "sm:h-auto" + (noMinHeight ? "" : " sm:min-h-[200px]"),
        "sm:max-h-[90vh]",
        // sm: Data state
        "sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%]",
        "sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]",
        // sm: Other
        "sm:rounded-lg sm:border sm:border-muted",
        "outline-none focus:outline-none",
        className
      )}
      aria-describedby={ariaDescribedBy ?? undefined}
      {...props}
    >
      {children}
      {showCloseButton && !noCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Mobile: Fixed/Flex
      "flex flex-col flex-shrink-0 " +
      // Mobile: Other
      "space-y-1.5 text-center mb-4 " +
      // sm: Other
      "sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Mobile: Fixed/Flex
      "flex flex-col-reverse flex-shrink-0 " +
      // Mobile: Other
      "mt-auto " +
      // sm: Fixed/Flex
      "sm:flex-row sm:justify-end " +
      // sm: Other
      "sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-foreground",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
